import React, { useRef, useState, useCallback } from 'react';
import { messagingService } from '../../api/services/messaging.service';

interface UploadedImage {
  id: number;
  url: string;
  width: number;
  height: number;
  size: number;
  is_wide: boolean;
}

interface ImageUploaderProps {
  siteId?: number;
  value?: UploadedImage | null;
  onChange?: (img: UploadedImage | null) => void;
  maxSizeMB?: number;
  accept?: string[]; // ['image/jpeg','image/png']
  externalError?: string | null;
  uploader?: (file: File, siteId?: number) => Promise<any>;
}

const prettySize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb/1024).toFixed(2)} MB`;
};

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  siteId,
  value,
  onChange,
  maxSizeMB = 1,
  accept = ['image/jpeg','image/png','image/gif'],
  externalError,
  uploader
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setError(null);
    if (!accept.includes(file.type)) {
      setError('허용되지 않는 이미지 형식입니다.');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`이미지 용량 초과 (최대 ${maxSizeMB}MB).`);
      return;
    }
    setUploading(true);
    try {
      const uploadFn = uploader || messagingService.uploadImage;
      const res = await uploadFn(file, siteId);
      const data = res?.data || res;
      if (onChange) onChange(data);
    } catch (e:any) {
      setError(e.message || '업로드 실패');
    } finally {
      setUploading(false);
    }
  }, [accept, maxSizeMB, onChange, siteId, uploader]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const removeImage = () => {
    if (onChange) onChange(null);
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  return (
    <div className="image-uploader">
      <input
        ref={inputRef}
        type="file"
        accept={accept.join(',')}
        style={{ display: 'none' }}
        onChange={onInputChange}
      />
      {!value && (
        <div
          onClick={openFileDialog}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          style={{
            border: '2px dashed #999',
            padding: '16px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragOver ? '#f5f5f5' : 'transparent'
          }}
        >
          {uploading ? '업로드 중...' : '이미지 드래그 또는 클릭하여 업로드'}
          <div style={{ fontSize: 12, marginTop: 8 }}>최대 {maxSizeMB}MB / 허용: {accept.map(a => a.split('/')[1]).join(', ')}</div>
          {(error || externalError) && <div style={{ color: 'red', marginTop: 8 }}>{externalError || error}</div>}
        </div>
      )}
      {value && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img src={value.url} alt="uploaded" style={{ maxWidth: '100%', borderRadius: 4, border: '1px solid #ccc' }} />
          <button type="button" onClick={removeImage} style={{ position: 'absolute', top: 4, right: 4 }}>✕</button>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {value.width}x{value.height}px • {prettySize(value.size)} {value.is_wide && '• WIDE'}
          </div>
          {externalError && <div style={{ color: 'red', marginTop: 4, fontSize: 12 }}>{externalError}</div>}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
